import { useState } from 'react';
import { Pencil, Loader2, Check, X } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { useUpdateSetting } from '../hooks/useUpdateSetting';
import type { SettingDto } from '../types/settings';

interface SettingFieldProps {
  setting: SettingDto;
  onUpdated: (setting: SettingDto) => void;
}

/**
 * Format a setting key as a human-readable label
 * e.g., "TaxRate" -> "Tax Rate", "CompanyName" -> "Company Name"
 */
const formatSettingLabel = (key: string): string => {
  return key.replace(/([A-Z])/g, ' $1').trim();
};

/**
 * Component for displaying and editing a single setting
 */
export function SettingField({ setting, onUpdated }: SettingFieldProps) {
  const [value, setValue] = useState(setting.value);
  const [isEditing, setIsEditing] = useState(false);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);

  const { update, isLoading, error, reset } = useUpdateSetting((updatedSetting) => {
    onUpdated(updatedSetting);
    setIsEditing(false);
    setSuccessMessage('Setting updated');
    setTimeout(() => setSuccessMessage(null), 2000);
  });

  const handleSave = async () => {
    reset();
    try {
      await update(setting.key, value);
    } catch {
      // Error is handled in the hook
    }
  };

  const handleCancel = () => {
    setValue(setting.value);
    setIsEditing(false);
    reset();
  };

  const isTaxRate = setting.key === 'TaxRate';

  const renderInput = () => {
    // Special handling for TaxRate - display as percentage
    if (isTaxRate) {
      const percentValue = (parseFloat(value) * 100).toFixed(2);
      return (
        <div className="flex items-center gap-1">
          <Input
            type="number"
            step="0.01"
            min="0"
            max="100"
            value={isEditing ? (parseFloat(value) * 100).toFixed(2) : percentValue}
            onChange={(e) => setValue((parseFloat(e.target.value) / 100).toString())}
            disabled={!isEditing}
            className="w-24"
            aria-label={formatSettingLabel(setting.key)}
          />
          <span className="text-muted-foreground">%</span>
        </div>
      );
    }

    switch (setting.dataType) {
      case 'decimal':
        return (
          <Input
            type="number"
            step="0.01"
            value={value}
            onChange={(e) => setValue(e.target.value)}
            disabled={!isEditing}
            className="w-32"
            aria-label={formatSettingLabel(setting.key)}
          />
        );
      case 'int':
        return (
          <Input
            type="number"
            step="1"
            value={value}
            onChange={(e) => setValue(e.target.value)}
            disabled={!isEditing}
            className="w-32"
            aria-label={formatSettingLabel(setting.key)}
          />
        );
      case 'bool':
        return (
          <Checkbox
            checked={value === 'true'}
            onChange={(e) => setValue(e.target.checked.toString())}
            disabled={!isEditing}
            aria-label={formatSettingLabel(setting.key)}
          />
        );
      default:
        return (
          <Input
            type="text"
            value={value}
            onChange={(e) => setValue(e.target.value)}
            disabled={!isEditing}
            className="w-80"
            aria-label={formatSettingLabel(setting.key)}
          />
        );
    }
  };

  return (
    <div className="flex items-center justify-between py-3 border-b last:border-0">
      <div className="flex-1 min-w-0">
        <Label className="font-medium text-sm">{formatSettingLabel(setting.key)}</Label>
        {setting.description && (
          <p className="text-sm text-muted-foreground mt-0.5">{setting.description}</p>
        )}
      </div>

      <div className="flex items-center gap-3 ml-4">
        {renderInput()}

        {error && (
          <span className="text-sm text-red-600">{error}</span>
        )}

        {successMessage && (
          <span className="text-sm text-green-600">{successMessage}</span>
        )}

        {isEditing ? (
          <>
            <Button
              size="sm"
              onClick={handleSave}
              disabled={isLoading}
              title="Save changes"
            >
              {isLoading ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                <Check className="h-4 w-4" />
              )}
            </Button>
            <Button
              size="sm"
              variant="outline"
              onClick={handleCancel}
              disabled={isLoading}
              title="Cancel"
            >
              <X className="h-4 w-4" />
            </Button>
          </>
        ) : (
          <Button
            size="sm"
            variant="ghost"
            onClick={() => setIsEditing(true)}
            title="Edit setting"
          >
            <Pencil className="h-4 w-4" />
          </Button>
        )}
      </div>
    </div>
  );
}
